import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, IdCard, FileText, ArrowLeftRight, UserPlus, Cog, AlertTriangle, TrendingUp,
  Send, Loader2, ShieldQuestion, CheckCircle2, ChevronDown, Mail,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const AUTH_ICONS = {
  payouts_over: DollarSign,
  verify_user_id: IdCard,
  view_documents: FileText,
  etransfers_over: ArrowLeftRight,
  driver_accounts: UserPlus,
  system_settings: Cog,
  fraud_alerts: AlertTriangle,
  financial_reports: TrendingUp,
};

const STATUS_STYLE = {
  pending: ["Pending", "#b45309", "#f59e0b1f"],
  approved: ["Approved", "#16a34a", "#16a34a1a"],
  denied: ["Denied", "#e4002b", "#e4002b14"],
  used: ["Used", "#565b73", "#565b7314"],
  expired: ["Expired", "#565b73", "#565b7314"],
};

const PRIORITY_STYLE = {
  normal: ["Normal", "#2340b5", "#2340b512"],
  important: ["Important", "#b45309", "#f59e0b1f"],
  urgent: ["Urgent", "#e4002b", "#e4002b14"],
};

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const initials = (name) => (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const Card = ({ title, right, children, testid }) => (
  <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm" data-testid={testid}>
    {(title || right) && (
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-[#12142b]">{title}</h2>
        {right}
      </div>
    )}
    {children}
  </div>
);

function ProfitCard() {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    adminApi.get("/finance/profit-breakdown", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setData({ summary: {} }));
  }, [period]);

  const s = data?.summary || {};
  const tiles = [
    { label: "Total income", value: money(s.total_income), color: "#2340b5", testid: "profit-total-income" },
    { label: "Refunded platform fees", value: money(s.refunds_chargebacks), color: "#b45309", testid: "profit-refunds" },
    { label: "Platform profit", value: money(s.profit), color: "#16a34a", testid: "profit-value" },
  ];
  const fees = [
    ["Booking fees", s.booking_fees], ["E-transfer fees", s.etransfer_fees],
    ["Ad fees", s.ad_fees], ["Other fees (pins)", s.pin_fees],
  ];

  return (
    <Card testid="platform-profit-card" title="Platform Profit"
      right={
        <div className="flex items-center gap-3">
          <Link to="/admin/finance-report" data-testid="profit-view-report" className="text-sm font-semibold text-[#2340b5] hover:underline">View Report</Link>
          <div className="flex gap-1 rounded-lg bg-[#f4f6fb] p-1" data-testid="profit-period-toggle">
            {[["7d", "7 days"], ["30d", "1 month"], ["1y", "1 year"]].map(([k, l]) => (
              <button key={k} data-testid={`profit-period-${k}`} onClick={() => setPeriod(k)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${period === k ? "bg-white text-[#2340b5] shadow-sm" : "text-[#565b73]"}`}>{l}</button>
            ))}
          </div>
        </div>
      }>
      {!data ? (
        <p className="py-6 text-center text-sm text-[#565b73]">Loading profit…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.2fr]">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {tiles.map((t) => (
              <div key={t.label} className="rounded-xl bg-[#f4f6fb] p-4" data-testid={t.testid}>
                <p className="font-display text-xl font-black tracking-tight" style={{ color: t.color }}>{t.value}</p>
                <p className="text-xs font-medium text-[#565b73]">{t.label}</p>
              </div>
            ))}
          </div>
          <div>
            {fees.map(([label, v]) => (
              <div key={label} className="flex items-center justify-between border-b border-[#12142b]/6 py-2.5 text-sm last:border-0">
                <span className="text-[#565b73]">{label}</span>
                <span className="font-semibold text-[#12142b]">{money(v)}</span>
              </div>
            ))}
            <p className="mt-2 text-xs text-[#565b73]/80">Platform profit = booking, e-transfer, ad and pin fees collected, minus refunded platform fees. Rider refunds return the riders' own share — the platform's fee is never refunded.</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function AuthRequestsCard() {
  const [items, setItems] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/authorization/requests").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await adminApi.post(`/authorization/requests/${review.id}/decision`, { decision });
      toast.success(decision === "approved" ? "Request authorized — one action, valid 15 minutes" : "Request denied");
      setReview(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update the request");
    } finally {
      setBusy(false);
    }
  };

  const rows = (items || []).slice(0, showAll ? 100 : 5);

  return (
    <Card testid="auth-requests-card" title="Recent Authorization Requests"
      right={(items || []).length > 5 && (
        <button data-testid="auth-view-all" onClick={() => setShowAll((v) => !v)} className="text-sm font-semibold text-[#2340b5] hover:underline">
          {showAll ? "Show less" : "View all"}
        </button>
      )}>
      {items === null ? (
        <p className="py-8 text-center text-sm text-[#565b73]">Loading requests…</p>
      ) : items.length === 0 ? (
        <p className="rounded-xl bg-[#f4f6fb] py-8 text-center text-sm text-[#565b73]" data-testid="auth-requests-empty">No authorization requests yet — they'll appear here when employees ask for access.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="auth-requests-table">
            <thead>
              <tr className="border-b border-[#12142b]/8 text-xs font-semibold uppercase tracking-wide text-[#565b73]">
                <th className="pb-3 font-semibold">Request Type</th>
                <th className="pb-3 font-semibold">Requested By</th>
                <th className="pb-3 font-semibold">Amount / Details</th>
                <th className="pb-3 font-semibold">Time</th>
                <th className="pb-3 font-semibold">Status</th>
                <th className="pb-3 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const Icon = AUTH_ICONS[r.action_key] || ShieldQuestion;
                const [label, color, bg] = STATUS_STYLE[r.status] || [r.status, "#565b73", "#565b7314"];
                const pending = r.status === "pending";
                return (
                  <tr key={r.id} data-testid={`auth-req-row-${r.id}`} className="border-b border-[#12142b]/5 last:border-0">
                    <td className="py-3.5">
                      <div className="flex items-center gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#2340b5]/8"><Icon className="h-4 w-4 text-[#2340b5]" /></span>
                        <span className="font-semibold text-[#12142b]">{r.action_label}</span>
                      </div>
                    </td>
                    <td className="py-3.5 text-[#565b73]">{r.requested_by_name || r.requested_by}</td>
                    <td className="max-w-[220px] truncate py-3.5 text-[#565b73]" title={r.note}>{r.note || "—"}</td>
                    <td className="py-3.5 text-[#565b73]">{fmtTime(r.created_at)}</td>
                    <td className="py-3.5">
                      <span data-testid={`auth-req-status-${r.id}`} className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color, backgroundColor: bg }}>{label}</span>
                    </td>
                    <td className="py-3.5 text-right">
                      <button
                        data-testid={pending ? `auth-review-${r.id}` : `auth-view-${r.id}`}
                        onClick={() => setReview(r)}
                        className={`rounded-lg px-4 py-2 text-xs font-bold transition-colors ${pending ? "border border-[#2340b5]/40 text-[#2340b5] hover:bg-[#2340b5]/8" : "border border-[#12142b]/12 text-[#565b73] hover:bg-[#f4f6fb]"}`}>
                        {pending ? "Review" : "View"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Review / view dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-md" data-testid="auth-review-dialog">
          <DialogHeader><DialogTitle>{review?.status === "pending" ? "Review request" : "Request details"}</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-3 text-sm">
              <div className="rounded-xl bg-[#f4f6fb] p-4">
                <p className="font-bold text-[#12142b]">{review.action_label}</p>
                <p className="mt-1 text-xs text-[#565b73]">Requested by <span className="font-semibold text-[#12142b]">{review.requested_by_name || review.requested_by}</span> · {fmtTime(review.created_at)}</p>
                {review.note && <p className="mt-2 text-[#565b73]">“{review.note}”</p>}
              </div>
              {review.status === "pending" ? (
                <p className="text-xs text-[#565b73]">Authorizing lets this employee perform the action <span className="font-semibold text-[#12142b]">once</span>, within <span className="font-semibold text-[#12142b]">15 minutes</span>.</p>
              ) : (
                <p className="text-xs text-[#565b73]">
                  {review.status === "approved" && review.grant_expires_at ? `Approved — access valid until ${fmtTime(review.grant_expires_at)}.` :
                   review.status === "used" ? "Approved and already used." :
                   review.status === "expired" ? "Approved but the 15-minute window expired unused." :
                   "This request was denied."}
                  {review.decided_by ? ` Decided by ${review.decided_by}.` : ""}
                </p>
              )}
            </div>
          )}
          {review?.status === "pending" && (
            <DialogFooter className="mt-2 gap-2 sm:justify-between">
              <button data-testid="auth-review-deny" onClick={() => decide("denied")} disabled={busy}
                className="rounded-xl border border-[#e4002b]/30 px-4 py-2.5 text-sm font-bold text-[#e4002b] hover:bg-[#e4002b]/8 disabled:opacity-50">Deny</button>
              <button data-testid="auth-review-approve" onClick={() => decide("approved")} disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#128a3e] disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Authorize
              </button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function NotesPanel() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);
  const [priority, setPriority] = useState("normal");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [notes, setNotes] = useState(null);
  const dropRef = useRef(null);

  const loadNotes = useCallback(() => {
    adminApi.get("/employees/notes").then((r) => setNotes(r.data.items || [])).catch(() => setNotes([]));
  }, []);

  useEffect(() => {
    adminApi.get("/employees").then((r) => setEmployees(r.data.items || [])).catch(() => setEmployees([]));
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    const close = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const send = async () => {
    if (!selected) return toast.error("Choose an employee first");
    if (!body.trim()) return toast.error("Write a note first");
    setSending(true);
    try {
      await adminApi.post(`/employees/${selected.id}/notes`, { priority, body: body.trim() });
      toast.success(`Note sent to ${selected.name}`);
      setBody("");
      setPriority("normal");
      loadNotes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the note");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Send note */}
      <Card testid="send-note-card">
        <div className="mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#12142b]" />
          <h2 className="font-display text-lg font-bold text-[#12142b]">Send note to employee</h2>
        </div>

        <label className="mb-1 block text-sm text-[#565b73]">To</label>
        <div className="relative" ref={dropRef}>
          <button type="button" data-testid="note-employee-select" onClick={() => setDropOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-xl border border-[#12142b]/12 bg-white px-4 py-3 text-left hover:border-[#2340b5]/40">
            {selected ? (
              <span className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">{initials(selected.name)}</span>
                <span>
                  <span className="block text-sm font-bold text-[#12142b]">{selected.name}</span>
                  <span className="block text-xs text-[#565b73]">{selected.role}</span>
                </span>
              </span>
            ) : (
              <span className="text-sm text-[#565b73]">Choose an employee…</span>
            )}
            <ChevronDown className="h-4 w-4 text-[#565b73]" />
          </button>
          {dropOpen && (
            <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-[#12142b]/10 bg-white shadow-lg" data-testid="note-employee-options">
              {employees.length === 0 && <p className="px-4 py-3 text-sm text-[#565b73]">No employees yet — add them from the Employees page.</p>}
              {employees.map((e) => (
                <button key={e.id} type="button" data-testid={`note-employee-option-${e.id}`}
                  onClick={() => { setSelected(e); setDropOpen(false); }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[#f4f6fb]">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">{initials(e.name)}</span>
                  <span>
                    <span className="block text-sm font-semibold text-[#12142b]">{e.name}</span>
                    <span className="block text-xs text-[#565b73]">{e.role} · {e.status}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="mb-1 mt-4 block text-sm text-[#565b73]">Priority</label>
        <div className="flex gap-2" data-testid="note-priority-group">
          {["normal", "important", "urgent"].map((p) => (
            <button key={p} type="button" data-testid={`note-priority-${p}`} onClick={() => setPriority(p)}
              className={`rounded-xl border px-5 py-2.5 text-sm font-semibold capitalize transition-colors ${priority === p ? "border-[#12142b] bg-[#12142b] text-white" : "border-[#12142b]/12 text-[#565b73] hover:border-[#12142b]/30"}`}>
              {p}
            </button>
          ))}
        </div>

        <label className="mb-1 mt-4 block text-sm text-[#565b73]">Note</label>
        <textarea
          data-testid="note-body" rows={4} maxLength={280} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Write a note for the employee…"
          className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-4 py-3 text-sm text-[#12142b] outline-none focus:border-[#2340b5]"
        />
        <div className="mt-1 flex items-center justify-between text-xs text-[#565b73]">
          <span>Visible to the employee and admins only.</span>
          <span data-testid="note-char-count">{body.length} / 280</span>
        </div>

        <button data-testid="note-send" onClick={send} disabled={sending || !selected || !body.trim()}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send note
        </button>
      </Card>

      {/* Recent notes */}
      <Card testid="recent-notes-card" title="Recent notes">
        {notes === null ? (
          <p className="py-8 text-center text-sm text-[#565b73]">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="rounded-xl bg-[#f4f6fb] py-8 text-center text-sm text-[#565b73]" data-testid="recent-notes-empty">No notes sent yet.</p>
        ) : (
          <div className="space-y-3" data-testid="recent-notes">
            {notes.map((n) => {
              const [label, color, bg] = PRIORITY_STYLE[n.priority] || PRIORITY_STYLE.normal;
              return (
                <div key={n.id} data-testid={`note-row-${n.id}`} className="rounded-xl border border-[#12142b]/8 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">{initials(n.to_name)}</span>
                      <div>
                        <p className="text-sm font-bold text-[#12142b]">{n.to_name}</p>
                        <p className="text-xs text-[#565b73]">{n.to_role} · {fmtTime(n.created_at)}</p>
                      </div>
                    </div>
                    <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color, backgroundColor: bg }}>{label}</span>
                  </div>
                  <p className="mt-2.5 text-sm text-[#3a3f57]">{n.body}</p>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function AdminDash() {
  return (
    <div data-testid="admin-dash-page">
      <PageHeader title="Admin Dash" subtitle="Authorization requests, employee notes and platform profit in one place." />
      <div className="space-y-6 p-8">
        <ProfitCard />
        <AuthRequestsCard />
        <NotesPanel />
      </div>
    </div>
  );
}
